import { useState, FormEvent } from 'react';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { formatCurrency, formatOdds, calculatePayout, parseBetAmount, validateBetAmount, getQuickBetAmounts } from '../../utils';
import { useParlay } from '../../store';
import type { Fight, Fighter, Participant } from '../../types';

interface BetFormProps {
  fight: Fight;
  participant: Participant;
  onBetPlaced: (bet: {
    fightId: string;
    fighterId: string;
    amount: number;
  }) => void;
  onCancel: () => void;
}

/**
 * Bet Form Component
 * Allows participant to select fighter, enter bet amount, and place bet
 * Now supports parlay mode!
 */
export function BetForm({ fight, participant, onBetPlaced, onCancel }: BetFormProps) {
  const [selectedFighter, setSelectedFighter] = useState<Fighter | null>(null);
  const [betAmount, setBetAmount] = useState('');
  const [error, setError] = useState('');
  
  const { isActive: parlayActive, startParlay, addLeg, hasFight } = useParlay();

  const availableBalance = participant.totalAvailable;
  const quickAmounts = getQuickBetAmounts(availableBalance);

  const handleFighterSelect = (fighter: Fighter) => {
    setSelectedFighter(fighter);
    setError('');
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBetAmount(e.target.value);
    setError('');
  };

  const handleQuickAmount = (amount: number) => {
    setBetAmount(amount.toString());
    setError('');
  };

  const handleStartParlay = () => {
    setError('');

    // Validate fighter selection
    if (!selectedFighter) {
      setError('Please select a fighter');
      return;
    }

    // Check if user entered an amount
    if (betAmount.trim()) {
      setError('Leave parlay amount empty until final bet is made.');
      return;
    }

    // Start parlay and add this as first leg
    startParlay();
    addLeg({
      fightId: fight.id,
      fightName: `${fight.fighters[0]?.name || 'TBD'} vs ${fight.fighters[1]?.name || 'TBD'}`,
      selection: selectedFighter.name,
      odds: selectedFighter.odds,
      type: 'moneyline',
    });

    // Navigate back or show confirmation
    onCancel();
  };

  const handleAddToParlay = () => {
    setError('');

    // Validate fighter selection
    if (!selectedFighter) {
      setError('Please select a fighter');
      return;
    }

    // Check if this fight is already in the parlay
    if (hasFight(fight.id)) {
      setError('This fight is already in your parlay. You can only add one bet per fight.');
      return;
    }

    // Add to existing parlay
    try {
      addLeg({
        fightId: fight.id,
        fightName: `${fight.fighters[0]?.name || 'TBD'} vs ${fight.fighters[1]?.name || 'TBD'}`,
        selection: selectedFighter.name,
        odds: selectedFighter.odds,
        type: 'moneyline',
      });

      // Navigate back
      onCancel();
    } catch (err: any) {
      setError(err.message || 'Failed to add to parlay');
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validate fighter selection
    if (!selectedFighter) {
      setError('Please select a fighter');
      return;
    }

    // Parse and validate bet amount
    const parsedAmount = parseBetAmount(betAmount);
    if (parsedAmount === null || parsedAmount <= 0) {
      setError('Please enter a valid bet amount');
      return;
    }

    if (!validateBetAmount(parsedAmount, 1.0, availableBalance)) {
      if (parsedAmount < 1.0) {
        setError('Minimum bet amount is $1.00');
      } else if (parsedAmount > availableBalance) {
        setError(`Insufficient funds. Available: ${formatCurrency(availableBalance)}`);
      } else {
        setError('Invalid bet amount');
      }
      return;
    }

    // Check if fight is still bettable
    if (fight.status !== 'upcoming') {
      setError('This fight is no longer accepting bets');
      return;
    }

    // Place bet
    onBetPlaced({
      fightId: fight.id,
      fighterId: selectedFighter.id,
      amount: parsedAmount,
    });
  };

  const parsedAmountForDisplay = parseBetAmount(betAmount);
  const potentialPayout = selectedFighter && parsedAmountForDisplay !== null && parsedAmountForDisplay > 0
    ? calculatePayout(parsedAmountForDisplay, selectedFighter.odds)
    : 0;

  return (
    <Card padding="lg">
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-primary-text mb-2">Place Bet</h2>
          <p className="text-secondary-text text-sm">
            Select a fighter and enter your bet amount
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Fighter Selection */}
          <div>
            <label className="block text-sm font-medium text-primary-text mb-3">
              Select Fighter
            </label>
            <div className="space-y-2">
              {fight.fighters.map((fighter) => (
                <button
                  key={fighter.id}
                  type="button"
                  onClick={() => handleFighterSelect(fighter)}
                  className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                    selectedFighter?.id === fighter.id
                      ? 'border-primary-blue bg-primary-blue/10'
                      : 'border-border bg-tertiary-bg hover:border-input-focus'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-primary-text">{fighter.name}</div>
                      <div className="text-sm text-secondary-text mt-1">
                        {formatOdds(fighter.odds)} • {calculatePayout(100, fighter.odds).toFixed(2)} payout per $100
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-primary-text text-lg">
                        {formatOdds(fighter.odds)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Bet Amount */}
          <div>
            <Input
              label="Bet Amount"
              type="text"
              placeholder="Enter amount (e.g., 100 or $100)"
              value={betAmount}
              onChange={handleAmountChange}
              error={error}
              helperText={`Available balance: ${formatCurrency(availableBalance)}`}
            />

            {/* Quick Bet Amounts & Parlay Button - Horizontal Layout */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/* Quick Amounts */}
              {quickAmounts.length > 0 && quickAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => handleQuickAmount(amount)}
                  className="px-3 py-1.5 text-sm bg-tertiary-bg border border-border rounded-lg text-primary-text hover:border-input-focus transition-colors"
                >
                  {amount === availableBalance ? 'All In' : formatCurrency(amount)}
                </button>
              ))}
              
              {/* Parlay Button - Always show, to the right of quick amounts */}
              {!parlayActive ? (
                <button
                  type="button"
                  onClick={handleStartParlay}
                  disabled={!selectedFighter}
                  className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                    selectedFighter
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  🎯 Start Parlay
                </button>
              ) : !hasFight(fight.id) ? (
                <button
                  type="button"
                  onClick={handleAddToParlay}
                  disabled={!selectedFighter}
                  className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                    selectedFighter
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  ➕ Add to Parlay
                </button>
              ) : (
                <span className="px-4 py-1.5 text-sm rounded-lg font-medium bg-gray-700 text-gray-500 cursor-not-allowed">
                  ✓ Already in Parlay
                </span>
              )}
            </div>
          </div>

          {/* Potential Payout */}
          {selectedFighter && parsedAmountForDisplay !== null && parsedAmountForDisplay > 0 && (
            <div className="p-4 bg-tertiary-bg rounded-lg border border-border">
              <div className="text-sm text-secondary-text mb-2">Potential Payout</div>
              <div className="text-2xl font-bold text-success-green">
                {formatCurrency(potentialPayout)}
              </div>
              <div className="text-xs text-muted-text mt-1">
                If {selectedFighter.name} wins
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-error/10 border border-error rounded-lg">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={onCancel}
            >
              Cancel
            </Button>
            {!parlayActive && (
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={!selectedFighter || !betAmount || !!error}
              >
                Place Bet
              </Button>
            )}
          </div>
        </form>
      </div>
    </Card>
  );
}

